/* RESPONSIBLE TEAM: team-pricing-and-packaging */
import Service from '@ember/service';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type IntlService from 'ember-intl/services/intl';
import type Workspace from 'embercom/objects/workspace';
import moment from 'moment-timezone';

interface SubscriptionSource {
  customerSubscriptionWillBeCancelledAt?: Date;
  customerHasSucceedingSubscription?: boolean;
  isSalesforceContracted?: boolean;
  inTrial?: boolean;
  customerId?: string;
  id?: string;
}

export default class SubscriptionEndingService extends Service {
  @service declare intl: IntlService;
  @service declare purchaseAnalyticsService: $TSFixMe;

  @tracked private source?: SubscriptionSource;

  initializeUsingApp(app: SubscriptionSource) {
    this.source = app;
  }

  initializeUsingWorkspace(workspace: Workspace) {
    this.source = workspace;
  }

  get customerSubscriptionWillBeCancelledAt(): Date | undefined {
    return this.source?.customerSubscriptionWillBeCancelledAt;
  }

  get hasSucceedingSubscription(): boolean | undefined {
    return this.source?.customerHasSucceedingSubscription;
  }

  get isSalesforceContracted(): boolean | undefined {
    return this.source?.isSalesforceContracted;
  }

  get inTrial(): boolean | undefined {
    return this.source?.inTrial;
  }

  get customerId(): string | undefined {
    return this.source?.customerId;
  }

  get appId(): string | undefined {
    return this.source?.id;
  }

  get customerSubscriptionCancelledWithin30Days() {
    if (!this.customerSubscriptionWillBeCancelledAt) {
      return false;
    }
    return moment(this.customerSubscriptionWillBeCancelledAt).isSameOrBefore(
      moment().add(30, 'days'),
    );
  }

  get customerSubscriptionCancelledAfter30Days() {
    if (!this.customerSubscriptionWillBeCancelledAt) {
      return false;
    }
    return moment(this.customerSubscriptionWillBeCancelledAt).isAfter(moment().add(30, 'days'));
  }

  get formattedCancellationDate() {
    if (!this.customerSubscriptionWillBeCancelledAt) {
      return null;
    }
    return this.intl.formatDate(this.customerSubscriptionWillBeCancelledAt, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }

  get longFormattedCancellationDate() {
    if (!this.customerSubscriptionWillBeCancelledAt) {
      return null;
    }
    return this.intl.formatDate(this.customerSubscriptionWillBeCancelledAt, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      // eslint-disable-next-line @intercom/intercom/no-bare-strings
      format: 'dd MMMM, yyyy',
    });
  }

  shouldShowSubscriptionEndingBanner(appWide: boolean) {
    if (
      !this.customerSubscriptionWillBeCancelledAt ||
      this.hasSucceedingSubscription ||
      this.inTrial
    ) {
      return false;
    }

    if (appWide) {
      return this.customerSubscriptionCancelledWithin30Days;
    }

    return this.customerSubscriptionCancelledAfter30Days;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'subscription-ending-service': SubscriptionEndingService;
  }
}
